import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { Link } from 'react-router-dom';
import { Add01Icon } from 'hugeicons-react';
import faqs, { Faq } from '../../Data/Faqs';
import '../../Styles/Pricing/FaqSection.css';

// Billing questions first; these are what people weigh up on the pricing page.
const PRICING_FAQ_IDS = [11, 2, 4, 13, 12, 3, 14, 5];

interface FaqSectionProps {
    faqs?: Faq[];
    title?: string;
}

const MarkdownLink = ({ href = '', children }: { href?: string; children?: React.ReactNode }) =>
    href.startsWith('/')
        ? <Link to={href}>{children}</Link>
        : <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>;

const FaqSection: React.FC<FaqSectionProps> = ({ faqs: customFaqs, title = 'Questions, Answered' }) => {
    const [activeFaqId, setActiveFaqId] = useState<number | null>(null);

    const displayFaqs = customFaqs
        ?? PRICING_FAQ_IDS.map(id => faqs.find(f => f.id === id)).filter((f): f is Faq => Boolean(f));

    return (
        <section className="pricing-faq" aria-labelledby="pricing-faq-heading">
            <h2 id="pricing-faq-heading">{title}</h2>
            <div className="faq-list">
                {displayFaqs.map((faq) => {
                    const open = activeFaqId === faq.id;
                    return (
                        <div key={faq.id} className={`faq-item ${open ? 'is-open' : ''}`}>
                            <h3 className="faq-heading">
                                <button
                                    type="button"
                                    className="faq-question"
                                    onClick={() => setActiveFaqId(open ? null : faq.id)}
                                    aria-expanded={open}
                                    aria-controls={`faq-answer-${faq.id}`}
                                    id={`faq-question-${faq.id}`}
                                >
                                    <span className="faq-question-text">{faq.question}</span>
                                    <Add01Icon size={18} className="faq-icon" aria-hidden />
                                </button>
                            </h3>
                            <div
                                id={`faq-answer-${faq.id}`}
                                className="faq-answer"
                                role="region"
                                aria-labelledby={`faq-question-${faq.id}`}
                                hidden={!open}
                            >
                                <ReactMarkdown components={{ a: MarkdownLink }}>
                                    {faq.answer}
                                </ReactMarkdown>
                            </div>
                        </div>
                    );
                })}
            </div>
            <p className="faq-footer">
                More questions? See the full <a href="https://cognora.ca/faq" target="_blank" rel="noopener noreferrer">FAQ</a>.
            </p>
        </section>
    );
};

export default FaqSection;
